import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { useActionItems, useBonusApplications } from "../../hooks/useBonusQueries";
import { ApplicationItem } from "../../components/ApplicationItem";
import { Breadcrumb } from "../../components/Breadcrumb";
import { RoleType } from "../../components/CreateButton/constants";
import { REVIEW_ITEMS_CONSTANTS } from "./constants";
import { BonusQueryParams, BonusApplication } from "../../services/bonus/types";
import { getPreviousQuarter } from "../../utils/dateUtils";
import { getBonusRecipientSearchBlob } from "../../utils/bonusRecipientSearch";
import {
  applicationStatusMatchesFilter,
  getApplicationStatusString,
} from "../../utils/applicationStatusFilter";
import { bonusApi } from "../../services/bonus/api";
import BonusApplicationDialog from "../../components/BonusApplicationDialog";
import PlusIcon from "@heroicons/react/20/solid/PlusIcon";
import { useEmployeePrefetch } from "../../hooks/useEmployeePrefetch";
import ClientSearchFilters, {
  ClientSearchFiltersState,
  ClientSearchFilterOption,
} from "../../components/ClientSearchFilters";
import ClientPagination from "../../components/ClientPagination";

const REVIEW_ITEMS_FILTERS_KEY_BASE = "reviewItemsFilters";
const LEGACY_REVIEW_ITEMS_FILTERS_KEY = "reviewItemsFilters";

const ReviewItems: React.FC = () => {
  const { accounts } = useMsal();
  const username = accounts[0]?.username || REVIEW_ITEMS_CONSTANTS.DEFAULT_USER.USERNAME;
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const selectedRole = localStorage.getItem("selectedRole") || "";
  const normalizedRole = selectedRole.toUpperCase();

  const reviewItemsFiltersKey =
    `${REVIEW_ITEMS_FILTERS_KEY_BASE}:${normalizedRole || "UNKNOWN"}`;

  const ccm3ApplicationTypeOptions: ClientSearchFilterOption[] = [
    { value: "AllCCM3Types", label: "All CCM3 Types" },
    { value: "CCM3", label: "CCM3" },
    { value: "CCM3Manager", label: "CCM3 Manager" },
  ];

  const recipientReviewStatusOptions: ClientSearchFilterOption[] = [
    { value: "Bonus_Recipient_Review", label: "Recipient Review" },
  ];

  const isCCM3Role = normalizedRole === "CCM3";
  const isDccmRole = normalizedRole === "DCCM" || normalizedRole === "DCCM-REVIEW";
  const isDmRole = normalizedRole === "DM" || normalizedRole === "DM-REVIEW";
  const isVpRole = normalizedRole === "VP" || normalizedRole === "VP-REVIEW";
  const isOpsRole = normalizedRole === "OPS";
  const isPayrollRole = normalizedRole === "PAYROLL";

  const lockedApplicationType =
    normalizedRole === "CCM3MANAGER"
      ? "CCM3Manager"
      : ["CCM1", "CCM2", "CM3", "PM", "SPM"].includes(normalizedRole)
        ? normalizedRole
        : null;

  const dccmApplicationTypeOptions: ClientSearchFilterOption[] = [
    { value: "AllCustomerCare", label: "All Customer Care" },
    { value: "CCM1", label: "CCM1" },
    { value: "CCM2", label: "CCM2" },
    { value: "CCM3", label: "CCM3" },
    { value: "CCM3Manager", label: "CCM3 Manager" },
    { value: "DCCM", label: "DCCM" },
  ];
  const dccmReviewStatusOptions: ClientSearchFilterOption[] = [
    { value: "All_Status", label: "All Status" },
    { value: "Bonus_Recipient_Review", label: "Recipient Review" },
    { value: "Draft", label: "Draft" },
    { value: "Submitted", label: "Submitted" },
    { value: "Rejected", label: "Rejected" },
  ];
  const dmApplicationTypeOptions: ClientSearchFilterOption[] = [
    { value: "AllConstruction", label: "All Construction" },
    { value: "CM3", label: "CM3" },
    { value: "PM", label: "PM" },
    { value: "SPM", label: "SPM" },
  ];
  const dmReviewStatusOptions: ClientSearchFilterOption[] = [
    { value: "All_Status", label: "All Status" },
    { value: "Draft", label: "Draft" },
    { value: "Submitted", label: "Submitted" },
    { value: "Rejected", label: "Rejected" },
  ];
  const vpReviewStatusOptions: ClientSearchFilterOption[] = [
    { value: "All_Status", label: "All Status" },
    { value: "Draft", label: "Draft" },
    { value: "Submitted", label: "Submitted" },
    { value: "Rejected", label: "Rejected" },
    { value: "VP_Review", label: "VP Review" },
  ];
  const opsReviewStatusOptions: ClientSearchFilterOption[] = [
    { value: "Ops_Review", label: "Ops Review" },
  ];
  const payrollReviewStatusOptions: ClientSearchFilterOption[] = [
    { value: "Payroll_Processing", label: "Payroll Processing" },
    { value: "Paid", label: "Paid" },
  ];

  const LOCKED_TYPE_LABELS: Record<string, string> = { CCM3Manager: "CCM3 Manager" };

  const defaultApplicationTypeOptions: ClientSearchFilterOption[] = [
    { value: "All", label: "All" },
    { value: "AllCustomerCare", label: "All Customer Care" },
    { value: "AllConstruction", label: "All Construction" },
    { value: "AllCCM3Types", label: "All CCM3 Types" },
    { value: "CCM1", label: "CCM1" },
    { value: "CCM2", label: "CCM2" },
    { value: "CCM3", label: "CCM3" },
    { value: "CCM3Manager", label: "CCM3 Manager" },
    { value: "DCCM", label: "DCCM" },
    { value: "CM3", label: "CM3" },
    { value: "PM", label: "PM" },
    { value: "SPM", label: "SPM" },
  ];  

  const applicationTypeOptionsForRole: ClientSearchFilterOption[] | undefined =
    lockedApplicationType
      ? [{ value: lockedApplicationType, label: LOCKED_TYPE_LABELS[lockedApplicationType] ?? lockedApplicationType }]
      : isCCM3Role
        ? ccm3ApplicationTypeOptions
        : isDccmRole
          ? dccmApplicationTypeOptions
          : isDmRole
            ? dmApplicationTypeOptions
            : defaultApplicationTypeOptions;

  const statusOptionsForRole: ClientSearchFilterOption[] =
    isDccmRole
      ? dccmReviewStatusOptions
      : isDmRole
        ? dmReviewStatusOptions
        : isVpRole
          ? vpReviewStatusOptions
          : isOpsRole
            ? opsReviewStatusOptions
            : isPayrollRole
              ? payrollReviewStatusOptions
              : recipientReviewStatusOptions;

  useEmployeePrefetch(selectedRole);

  const [showCreateBonusButton, setShowCreateBonusButton] = useState<boolean>(false);
  const [showCreateForExEmployees, setShowCreateForExEmployees] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<'approve' | 'reject' | 'payroll' | null>(null);
  const [showCreateBonusDialog, setShowCreateBonusDialog] = useState(false);

  const [showBonusApplicationDialog, setShowBonusApplicationDialog] = useState(false);

  const getInitialFilters = (): BonusQueryParams => {
    const pq = getPreviousQuarter();
    return {
      year: "All",
      quarter: "All",
    };
  };

  const getDefaultClientFilters = (): ClientSearchFiltersState => ({
    year: "All",
    quarter: "All",

    applicationType: isDccmRole
      ? "AllCustomerCare"
      : isDmRole
        ? "AllConstruction"
        : isCCM3Role
          ? "AllCCM3Types"
          : lockedApplicationType ?? "All",

    status: isDccmRole
      ? "All_Status"
      : isDmRole
        ? "All_Status"
        : isVpRole
          ? "All_Status"
          : isOpsRole
            ? "Ops_Review"
            : isPayrollRole
              ? "Payroll_Processing"
              : "Bonus_Recipient_Review",

    bonusRecipient: "",
    region: "All",
    pageSize: 25,
  });

  const getInitialClientFilters = (): ClientSearchFiltersState => {
    const defaults = getDefaultClientFilters();

    const stored = localStorage.getItem(reviewItemsFiltersKey);

    if (!stored) {
      return defaults;
    }

    try {
      const parsed =
        JSON.parse(stored) as Partial<ClientSearchFiltersState>;

      const allowedApplicationTypes =
        applicationTypeOptionsForRole?.map(o => o.value);

      const allowedStatuses =
        statusOptionsForRole.map(o => o.value);

      const applicationType =
        lockedApplicationType ??
        (
          allowedApplicationTypes &&
            !allowedApplicationTypes.includes(parsed.applicationType ?? "")
            ? defaults.applicationType
            : parsed.applicationType ?? defaults.applicationType
        );

      const status =
        allowedStatuses.includes(parsed.status ?? "")
          ? parsed.status!
          : defaults.status;

      const pageSize =
        [10, 25, 50, 100].includes(parsed.pageSize ?? 0)
          ? parsed.pageSize!
          : defaults.pageSize;

      return {
        ...defaults,
        ...parsed,

        // Validated/authoritative fields must come AFTER parsed.
        applicationType,
        status,
        pageSize,
      };
    } catch (e) {
      console.error(
        "Failed to parse stored review items filters:",
        e
      );

      localStorage.removeItem(reviewItemsFiltersKey);

      return defaults;
    }
  };

  const [clientFilters, setClientFilters] = useState<ClientSearchFiltersState>(getInitialClientFilters);
  const [appliedClientFilters, setAppliedClientFilters] = useState<ClientSearchFiltersState>(getInitialClientFilters);

  const [filters, setFilters] = useState<BonusQueryParams>(getInitialFilters);
  const [page, setPage] = useState(1);

  const [filtersRole, setFiltersRole] = useState(normalizedRole);

  React.useEffect(() => {
    setFilters({
      year: clientFilters.year === "All" ? "All" : clientFilters.year,
      quarter: clientFilters.quarter === "All" ? "All" : clientFilters.quarter,
    });
  }, [clientFilters.year, clientFilters.quarter]);

  // Fetch data to check create bonus eligibility
  const { data: dataToCheckCreateBonusEligibility } = useBonusApplications({
    year: "All",
    quarter: "All",
  });

  React.useEffect(() => {
    localStorage.removeItem("search_filters_action_items");
    localStorage.removeItem(LEGACY_REVIEW_ITEMS_FILTERS_KEY);
  }, []);

  // Check if Create Bonus button should be shown
  React.useEffect(() => {
    let actionItems = Array.isArray(dataToCheckCreateBonusEligibility?.data)
      ? dataToCheckCreateBonusEligibility?.data
      : [];

    // Filter action items based on role
    actionItems = actionItems?.filter(
      (item) => item.applicationType === selectedRole.toString()
    );

    const hasActionItems = (actionItems?.length ?? 0) > 0;

    const alwaysEligibleRoles = new Set([
      RoleType.CM3.toString(),
      RoleType.PM.toString(),
      RoleType.SPM.toString()
    ]);

    // Only one CCM role allowed to create when there are no action items
    const conditionalRoles1 = new Set([
      RoleType.CCM1.toString(),
      RoleType.CCM2.toString(),
      RoleType.CCM3.toString()
    ]);

    // Only one DCCM role allowed to create when there are no action items
    const conditionalRoles2 = new Set([
      RoleType.DCCM.toString()
    ]);

    const shouldShow = alwaysEligibleRoles.has(selectedRole)
      || (conditionalRoles1.has(selectedRole) && !hasActionItems)
      || (conditionalRoles2.has(selectedRole) && !hasActionItems);

    setShowCreateBonusButton(shouldShow);
  }, [selectedRole, dataToCheckCreateBonusEligibility]);

  React.useEffect(() => {
    if (filtersRole === normalizedRole) {
      return;
    }

    const nextFilters = getInitialClientFilters();
    setClientFilters(nextFilters);
    setAppliedClientFilters(nextFilters);

    setFilters({
      year: nextFilters.year,
      quarter: nextFilters.quarter,
    });

    setPage(1);
    setFiltersRole(normalizedRole);
  }, [normalizedRole, filtersRole]);

  React.useEffect(() => {
    if (filtersRole !== normalizedRole) return;

    localStorage.setItem(
      reviewItemsFiltersKey,
      JSON.stringify(clientFilters)
    );
  }, [clientFilters, reviewItemsFiltersKey, normalizedRole, filtersRole]);

  const {
    data: apiResponse,
    isLoading,
    error,
  } = useActionItems(filters);

  const actionItems: BonusApplication[] = apiResponse?.data || [];

  const KNOWN_REGIONS = ["Houston", "Austin", "San Antonio", "DFW", "Other"];
  const getRegion = (app: BonusApplication): string => app.location || "";

  const filteredActionItems = actionItems.filter((app) => {
    const appType = app.applicationType || "";
    const bonusRecipientBlob = getBonusRecipientSearchBlob(app);
    const region = getRegion(app);

    const appTypeMatch =
      appliedClientFilters.applicationType === "All" ||
      (appliedClientFilters.applicationType === "AllCCM3Types" && ["CCM3", "CCM3Manager"].includes(appType)) ||
      (appliedClientFilters.applicationType === "AllConstruction" && ["CM3", "PM", "SPM"].includes(appType)) ||
      (appliedClientFilters.applicationType === "AllCustomerCare" && ["CCM1", "CCM2", "CCM3", "CCM3Manager", "DCCM"].includes(appType)) ||
      appType === appliedClientFilters.applicationType;

    const statusMatch = applicationStatusMatchesFilter(
      getApplicationStatusString(app),
      appliedClientFilters.status
    );
    const bonusRecipientMatch =
      !appliedClientFilters.bonusRecipient ||
      bonusRecipientBlob.includes(appliedClientFilters.bonusRecipient.toLowerCase());

    const regionMatch =
      appliedClientFilters.region === "All" ||
      (appliedClientFilters.region === "Other"
        ? !KNOWN_REGIONS.includes(region) && region !== ""
        : region === appliedClientFilters.region);
    const yearMatch =
      appliedClientFilters.year === "All" || app.year === appliedClientFilters.year;
    const quarterMatch =
      appliedClientFilters.quarter === "All" || app.quarter === appliedClientFilters.quarter;

    return appTypeMatch && statusMatch && bonusRecipientMatch && regionMatch && yearMatch && quarterMatch;
  });

  const paginatedActionItems = filteredActionItems.slice(
    (page - 1) * appliedClientFilters.pageSize,
    page * appliedClientFilters.pageSize
  );

  const handleFilterChange = (newFilters: BonusQueryParams) => {
    console.log('Filter change received:', newFilters);
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    const pq = getPreviousQuarter();
    setFilters({ year: "All", quarter: "All" });
  };

  const breadcrumbPages = [
    {
      name: REVIEW_ITEMS_CONSTANTS.BREADCRUMB.TITLE,
      href: REVIEW_ITEMS_CONSTANTS.BREADCRUMB.PATH,
      current: true,
      icon: (props: React.ComponentProps<"svg">) => (
        <ClipboardDocumentListIcon
          {...props}
          className="w-6 h-6 text-primary-500 transition duration-75 group-hover:text-gray-900"
        />
      ),
    },
  ];

  const toggleRowExpansion = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCheckboxChange = (id: number, isChecked: boolean) => {
    setSelectedItems((prev) => {
      if (isChecked) {
        return [...prev, id];
      } else {
        return prev.filter((itemId) => itemId !== id);
      }
    });
  };

  const handleBulkAction = (action: 'approve' | 'reject' | 'payroll') => {
    setPendingAction(action);
    setShowConfirmDialog(true);
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;

    if (pendingAction === 'payroll') {
      try {
        for (const id of selectedItems) {
          const payload = {
            UserAction: "Payroll_Processing" as const,
            Amount: 0,
            Comments: [],
            BonusSheet: "{}",
            ExEmployee: null
          };
          await bonusApi.editBonusApplication({
            BonusApplicationId: id,
            ...payload
          });
        }
        window.location.reload();
      } catch (error) {
        console.error("Error during bulk payroll processing:", error);
      } finally {
        setShowConfirmDialog(false);
        setPendingAction(null);
      }
      return;
    }

    const comments: string[] = [`Bulk action performed by ${username}`];
    if (pendingAction === 'approve') {
      comments.push("Approved by Ops");
    } else if (pendingAction === 'reject') {
      comments.push("Rejected by Ops");
    }

    const payload = {
      comments,
      applications: selectedItems.map((id) => {
        const item = actionItems.find((actionItem) => actionItem.bonusApplicationId === id);
        return {
          BonusApplicationId: id,
          UserAction: pendingAction === 'approve' ? "Approved" as const : "Rejected" as const,
          Amount: item?.amount || null,
          BonusSheet: item?.bonusSheet || null,
          ExEmployee: null, // BonusApplication doesn't have exEmployee, set to null or omit
          Comments: []
        };
      })
    };

    try {
      await bonusApi.bulkUpdateBonusApplications(payload);
      window.location.reload();
    } catch (error) {
      console.error(`Error during bulk ${pendingAction}:`, error);
    } finally {
      setShowConfirmDialog(false);
      setPendingAction(null);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmDialog(false);
    setPendingAction(null);
  };

  const getErrorMessage = (err: any): string => {
    if (typeof err === "string") return err;
    if (err?.message) return err.message;
    return "An unknown error occurred";
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="xl:pl-0 flex-grow flex flex-col">
        <main className="flex-grow flex flex-col overflow-hidden">
          <div className="relative isolate overflow-hidden pt-6 flex-grow flex flex-col">
            <div
              className={`mx-auto w-full ${REVIEW_ITEMS_CONSTANTS.LAYOUT.MAX_WIDTH} ${REVIEW_ITEMS_CONSTANTS.LAYOUT.PADDING}`}
            >
              {/* Breadcrumb */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <Breadcrumb pages={breadcrumbPages} />
                  <div className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                    {filteredActionItems.length} {REVIEW_ITEMS_CONSTANTS.HEADER.COUNTER_TEXT}
                  </div>
                </div>
                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  {((selectedRole === "DCCM" ||
                    selectedRole === "DCCM-Review" ||
                    selectedRole === "DM-Review" ||
                    selectedRole === "DM" ||
                    selectedRole === "VP-Review" ||
                    selectedRole === "VP") &&

                    <button
                      onClick={() => setShowBonusApplicationDialog(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg hover:opacity-90 transition-opacity duration-150"
                      style={{ background: "var(--primary)", color: "var(--primary-foreground)" }}
                    >
                      <PlusIcon className="w-4 h-4" />
                      Create Bonus Application
                    </button>)}

                  {/* {showCreateBonusButton && <CreateBonusButton actionItem={true} />}
                  {showCreateForExEmployees && (
                    <ExEmployeeDropdown
                      exEmployees={exEmployees}
                      onSelectExEmployee={handleSelectExEmployee}
                    />
                  )} */}
                </div>
              </div>

              <ClientSearchFilters
                filters={clientFilters}

                onChange={(next) => {
                  setClientFilters(next);

                  if (next.pageSize !== appliedClientFilters.pageSize) {
                    setPage(1);
                  }

                  setAppliedClientFilters(next);
                  setPage(1);
                }}

                onSearch={() => {
                  setAppliedClientFilters(clientFilters);
                  setPage(1);
                }}

                onClear={() => {
                  const clear = getDefaultClientFilters();

                  localStorage.removeItem(reviewItemsFiltersKey);

                  setClientFilters(clear);
                  setAppliedClientFilters(clear);
                  setFilters({
                    year: "All",
                    quarter: "All",
                  });
                  setPage(1);
                }}

                isLoading={isLoading}
                selectedRole={selectedRole}

                applicationTypeOptions={applicationTypeOptionsForRole}
                statusOptions={statusOptionsForRole}

                statusMode="all"
              />

              {/* Bulk Actions */}
              {selectedRole === "Ops" && filteredActionItems.length > 0 && (
                <div className="flex justify-end space-x-4 mb-4 mt-4">
                  <button
                    className={`px-4 py-2 rounded-md text-white bg-green-500 ${selectedItems.length > 0 ? "hover:bg-green-600" : "cursor-not-allowed opacity-50"}`}
                    onClick={() => handleBulkAction('approve')}
                    disabled={selectedItems.length === 0}
                  >
                    Bulk Approve
                  </button>
                  <button
                    className={`px-4 py-2 rounded-md ${selectedItems.length > 0 ? "hover:opacity-90" : "cursor-not-allowed opacity-50"}`}
                    style={{ background: "var(--destructive)", color: "var(--primary-foreground)" }}
                    onClick={() => handleBulkAction('reject')}
                    disabled={selectedItems.length === 0}
                  >
                    Bulk Reject
                  </button>
                </div>
              )}

              <BonusApplicationDialog
                open={showBonusApplicationDialog}
                onClose={() => setShowBonusApplicationDialog(false)}
              />

              {/* Confirmation Dialog */}
              <Dialog
                open={showConfirmDialog}
                onClose={handleCancelAction}
                aria-labelledby="confirm-dialog-title"
              >
                <DialogTitle id="confirm-dialog-title">
                  Confirm {pendingAction === 'approve' ? 'Approval' : pendingAction === 'reject' ? 'Rejection' : 'Payment Processing'}
                </DialogTitle>
                <DialogContent>
                  Are you sure you want to {pendingAction === 'approve' ? 'approve' : pendingAction === 'reject' ? 'reject' : 'process payment for'} {selectedItems.length} selected item{selectedItems.length !== 1 ? 's' : ''}?
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCancelAction} color="primary">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmAction}
                    color={pendingAction === 'approve' ? 'success' : pendingAction === 'reject' ? 'error' : 'primary'}
                    variant="contained"
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Content */}
              {isLoading ? (
                <div className="flex justify-center mt-8">
                  <CircularProgress />
                </div>
              ) : error ? (
                <div className="text-center mt-8" style={{ color: "var(--destructive)" }}>
                  {error instanceof Error
                    ? error.message
                    : `${REVIEW_ITEMS_CONSTANTS.STATES.ERROR.MESSAGE}${error as string}`}
                </div>
              ) : filteredActionItems.length === 0 ? (
                <div className="text-center p-8 rounded-lg mt-8" style={{ color: "var(--muted-foreground)", background: "var(--muted)" }}>
                  <ClipboardDocumentListIcon
                    className={`${REVIEW_ITEMS_CONSTANTS.STATES.EMPTY.ICON.SIZE} ${REVIEW_ITEMS_CONSTANTS.STATES.EMPTY.ICON.COLOR} mx-auto mb-4`}
                  />
                  <p className="text-lg font-medium">
                    {REVIEW_ITEMS_CONSTANTS.STATES.EMPTY.TITLE}
                  </p>
                  <p className="text-sm">
                    {REVIEW_ITEMS_CONSTANTS.STATES.EMPTY.SUBTITLE}
                    {showCreateBonusButton ? " or create a new bonus application." : "."}
                  </p>
                </div>
              ) : (
                <div className={REVIEW_ITEMS_CONSTANTS.LAYOUT.SPACING.CONTENT}>
                  {paginatedActionItems.map((item: BonusApplication) => (
                    <ApplicationItem
                      key={item.bonusApplicationId}
                      app={item}
                      expandedRows={expandedRows}
                      toggleRowExpansion={toggleRowExpansion}
                      roleType={RoleType[selectedRole.replace("-", "_") as keyof typeof RoleType]}
                      isReviewMode={true}
                      showCheckbox={selectedRole === "Ops"}
                      isChecked={selectedItems.includes(item.bonusApplicationId)}
                      onCheckboxChange={handleCheckboxChange}
                      sourcePath="/review-items"
                    />
                  ))}
                </div>
              )}
              <ClientPagination
                page={page}
                pageSize={appliedClientFilters.pageSize}
                totalCount={filteredActionItems.length}
                onPageChange={setPage}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ReviewItems;